/**
 * Delta-Based Restore for Excel
 *
 * Reverses a delta chain by walking it backwards and restoring
 * each tool's beforeCells. Much faster than full snapshot restore
 * since it only touches the cells that were actually changed.
 */

import {
  columnLetterToNumber,
  columnNumberToLetter,
  parseCellAddress,
} from '../agents/tool-utils'
import { getExcelRuntime } from '../excel-utils'
import type { CellSnapshot, TurnDeltaChain } from './snapshot-db'

/**
 * Restore a workbook to its pre-turn state using the delta chain.
 * Walks the chain in reverse order, restoring each delta's beforeCells.
 *
 * Returns true if restore succeeded, false if caller should fall back
 * to full snapshot restore.
 */
export async function restoreFromDeltaChainExcel(
  chain: TurnDeltaChain
): Promise<boolean> {
  if (!chain.isComplete) {
    console.warn(
      '[DeltaRestore] Chain is incomplete, falling back to full snapshot'
    )
    return false
  }

  if (chain.deltas.length === 0) {
    console.log('[DeltaRestore] No deltas to restore')
    return true
  }

  const Excel = getExcelRuntime()

  try {
    // Collect all beforeCells from all deltas (reverse order for correct layering)
    // If the same cell was modified by multiple tools, we want the FIRST delta's
    // beforeCells (the original state before any tool ran)
    const cellsToRestore = new Map<string, CellSnapshot>()

    // Process in forward order — first delta has the original state
    for (const delta of chain.deltas) {
      for (const [key, cell] of Object.entries(delta.beforeCells)) {
        // Only keep the FIRST occurrence (original pre-turn state)
        if (!cellsToRestore.has(key)) {
          cellsToRestore.set(key, cell)
        }
      }
    }

    if (cellsToRestore.size === 0) {
      console.log('[DeltaRestore] No cells to restore')
      return true
    }

    console.log(
      `[DeltaRestore] Restoring ${cellsToRestore.size} cells from ${chain.deltas.length} deltas`
    )

    // Group cells by sheet for batch writing
    const cellsBySheet = new Map<
      string,
      Array<{ address: string; cell: CellSnapshot }>
    >()

    for (const [key, cell] of cellsToRestore) {
      // Key format: "SheetName!CellAddress"
      const bangIdx = key.indexOf('!')
      if (bangIdx === -1) continue

      const sheetName = key.substring(0, bangIdx)
      const cellAddress = key.substring(bangIdx + 1)

      if (!cellsBySheet.has(sheetName)) {
        cellsBySheet.set(sheetName, [])
      }
      cellsBySheet.get(sheetName)?.push({ address: cellAddress, cell })
    }

    await Excel.run(async (context: Excel.RequestContext) => {
      for (const [sheetName, cells] of cellsBySheet) {
        const sheet = context.workbook.worksheets.getItem(sheetName)

        // Calculate bounding box for batch write
        let minRow = Number.MAX_SAFE_INTEGER
        let maxRow = 0
        let minCol = Number.MAX_SAFE_INTEGER
        let maxCol = 0

        for (const { address } of cells) {
          const { col, row } = parseCellAddress(address)
          const colNum = columnLetterToNumber(col)
          minRow = Math.min(minRow, row)
          maxRow = Math.max(maxRow, row)
          minCol = Math.min(minCol, colNum)
          maxCol = Math.max(maxCol, colNum)
        }

        const rowCount = maxRow - minRow + 1
        const colCount = maxCol - minCol + 1

        // Build 2D values, formulas, and numberFormats arrays
        const values: unknown[][] = Array.from({ length: rowCount }, () =>
          Array.from({ length: colCount }, () => '')
        )
        const formulas: (string | null)[][] = Array.from(
          { length: rowCount },
          () => Array.from({ length: colCount }, () => null)
        )
        const numberFormats: string[][] = Array.from({ length: rowCount }, () =>
          Array.from({ length: colCount }, () => 'General')
        )
        let hasFormulas = false
        let hasNumberFormats = false

        for (const { address, cell } of cells) {
          const { col, row } = parseCellAddress(address)
          const colNum = columnLetterToNumber(col)
          const rowIndex = row - minRow
          const colIndex = colNum - minCol

          values[rowIndex][colIndex] = cell.value
          if (cell.formula) {
            formulas[rowIndex][colIndex] = cell.formula
            hasFormulas = true
          }

          if (cell.format.numberFormat) {
            numberFormats[rowIndex][colIndex] = cell.format.numberFormat
            hasNumberFormats = true
          }
        }

        const startAddress = `${columnNumberToLetter(minCol)}${minRow}`
        const endAddress = `${columnNumberToLetter(maxCol)}${maxRow}`
        const rangeAddress =
          startAddress === endAddress
            ? startAddress
            : `${startAddress}:${endAddress}`

        const range = sheet.getRange(rangeAddress)
        range.values = values

        if (hasFormulas) {
          const formulasArray: string[][] = formulas.map((row, r) =>
            row.map((f, c) => f ?? String(values[r][c] ?? ''))
          )
          range.formulas = formulasArray
        }

        // Restore number formats (skip if all "General")
        if (hasNumberFormats) {
          range.numberFormat = numberFormats
        }
      }

      await context.sync()
    })

    console.log(
      `[DeltaRestore] Successfully restored ${cellsToRestore.size} cells`
    )
    return true
  } catch (error) {
    console.error('[DeltaRestore] Failed to restore from delta chain', error)
    return false
  }
}
